import { useState, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance, GameZone } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

const ZONE_LABELS: Record<'graveyard' | 'exile', string> = {
  graveyard: 'Graveyard',
  exile: 'Exile',
};

interface ZoneCardRowProps {
  instance: CardInstance;
  zone: GameZone;
  onHover: (card: CardInstance | null) => void;
}

function ZoneCardRow({ instance, zone, onHover }: ZoneCardRowProps) {
  const artUri = getCardImageUri(instance.card, 'artCrop');
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${zone}-${instance.instanceId}`,
    data: { instanceId: instance.instanceId, zone },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseEnter={() => onHover(instance)}
      onMouseLeave={() => onHover(null)}
      className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-700/60 cursor-grab active:cursor-grabbing transition ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      {artUri ? (
        <img src={artUri} alt={instance.card.name} className="w-8 h-8 object-cover rounded shrink-0 border border-gray-700" />
      ) : (
        <div className="w-8 h-8 bg-gray-800 rounded shrink-0 border border-gray-700" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200 truncate">{instance.card.name}</div>
        <div className="text-[10px] text-gray-500 truncate">{instance.card.typeLine}</div>
      </div>
    </div>
  );
}

interface ZoneListPanelProps {
  zone: 'graveyard' | 'exile';
}

export default function ZoneListPanel({ zone }: ZoneListPanelProps) {
  const cards = usePlaytesterStore((s) => s[zone]);
  const setOpenPanel = usePlaytesterStore((s) => s.setOpenPanel);
  const setHoveredCard = usePlaytesterStore((s) => s.setHoveredCard);
  const [filter, setFilter] = useState('');
  // Panel rows update the global hoveredCard — the global CardHoverPreview
  // handles display (no inline preview here).
  const handleHover = useCallback(
    (instance: CardInstance | null) => {
      setHoveredCard(instance?.card ?? null);
    },
    [setHoveredCard],
  );

  const filtered = filter
    ? cards.filter((i) => i.card.name.toLowerCase().includes(filter.toLowerCase()))
    : cards;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setOpenPanel(null)}>
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[480px] max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-white">
            {ZONE_LABELS[zone]} <span className="text-gray-500 font-normal">({cards.length})</span>
          </h2>
          <button
            onClick={() => setOpenPanel(null)}
            className="text-gray-500 hover:text-white transition text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-800 shrink-0">
          <input
            type="text"
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
            autoFocus
          />
        </div>

        {/* Card list — hover feeds global CardHoverPreview (no inline preview) */}
        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="text-center text-gray-600 text-sm py-8">
              {filter ? 'No matches' : 'Empty'}
            </div>
          ) : (
            filtered.map((instance) => (
              <ZoneCardRow
                key={instance.instanceId}
                instance={instance}
                zone={zone}
                onHover={handleHover}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
